import { useEffect } from 'react';
import './vitrine.css';
import { C } from '../theme';
import { EnTete, Lien, Pied } from './composants';
import { CHEMIN_DEMO } from '../routeur';

/**
 * Politique de confidentialite.
 *
 * Ce document decrit ce que Colette FAIT REELLEMENT aujourd'hui, verifie contre le code :
 * le schema de la base (packages/db/prisma/schema.prisma), les routes de l'API, le
 * routeur IA (packages/core) et la sauvegarde installee sur le serveur. Toute evolution
 * du traitement des donnees doit etre repercutee ICI le jour meme.
 *
 * Les mentions legales ont ete donnees par le porteur le 2026-08-28 : le projet est porte
 * par sa micro-entreprise francaise. Le bandeau « brouillon » a donc ete retire — la
 * condition qui le justifiait est levee.
 *
 * Ce qui reste, et qui n'a pas sa place sur une page publique : une relecture juridique
 * serait prudente avant d'ouvrir Colette au-dela des proches. C'est consigne dans
 * CLAUDE.md, pas ici — le visiteur n'a pas a lire nos reserves internes.
 */

const DERNIERE_MISE_A_JOUR = '28 août 2026';

/**
 * Les mentions legales du responsable de traitement, donnees par le porteur le
 * 2026-08-28. Elles vivent ici, en un seul endroit : elles apparaissent a trois
 * endroits du document, et trois copies divergeraient un jour.
 *
 * Le projet est porte par une MICRO-ENTREPRISE francaise. Pour un entrepreneur
 * individuel, la raison sociale est le nom de la personne — c'est pourquoi elle ne peut
 * venir que de lui, et jamais d'une deduction.
 */
const RAISON_SOCIALE = 'Sismaila RAMLI';
const EMAIL_CONTACT = 'ramliconnecte@gmail.com';
const ADRESSE_POSTALE = '40 rue Petit, 75019 Paris';

export function Confidentialite() {
  // Ce document se LIT, longuement. Il force donc le theme clair, quel que soit le choix
  // fait ailleurs : plusieurs centaines de lignes en blanc sur un ciel sombre fatiguent,
  // et un texte juridique doit d'abord etre lisible. Le theme est rendu en quittant la
  // page, sinon le choix de l'utilisateur serait silencieusement ecrase.
  useEffect(() => {
    const racine = document.documentElement;
    const precedent = racine.getAttribute('data-theme');
    racine.setAttribute('data-theme', 'blanc');
    return () => {
      if (precedent === null) racine.removeAttribute('data-theme');
      else racine.setAttribute('data-theme', precedent);
    };
  }, []);

  return (
    <div className="v-page">
      <EnTete
        actionPrincipale={
          <Lien vers="/" className="v-bouton v-bouton--secondaire v-bouton--compact">
            Retour à l'accueil
          </Lien>
        }
      />

      <main className="v-section v-section--serree">
        <div className="v-contenu v-contenu--etroit v-doc">
          <h1 className="v-h1" style={{ fontSize: 'clamp(1.875rem, 4.5vw, 2.75rem)' }}>
            Politique de confidentialité
          </h1>
          <p style={{ marginTop: 'var(--e3)', color: C.tertiaire, fontSize: '0.875rem' }}>
            Dernière mise à jour : {DERNIERE_MISE_A_JOUR}
          </p>

          <div className="v-encart">
            <p>
              En bref : Colette ne lit que ce que tu lui donnes, n'agit jamais sans ta
              validation, ne vend rien à personne, et tes données ne servent à entraîner
              aucune intelligence artificielle.
            </p>
          </div>

          <h2>1. Qui est responsable de tes données</h2>
          <p>
            Le responsable du traitement est <strong>{RAISON_SOCIALE}</strong>, entrepreneur
            individuel, dont le siège est situé {ADRESSE_POSTALE}. Pour toute question ou pour
            exercer tes droits, écris à{' '}
            <a href={`mailto:${EMAIL_CONTACT}`}>{EMAIL_CONTACT}</a>.
          </p>

          <h2>2. Quelles données Colette conserve</h2>

          <h3>Ton compte</h3>
          <ul>
            <li>Ton nom d'affichage et ton adresse email.</li>
            <li>
              Ton mot de passe, <strong>jamais en clair</strong> : seule une empreinte
              cryptographique (Argon2) est enregistrée. Personne, pas même nous, ne peut le
              relire.
            </li>
            <li>
              Des jetons de session, qui te maintiennent connecté et que tu peux révoquer en te
              déconnectant.
            </li>
          </ul>

          <h3>Ton foyer</h3>
          <ul>
            <li>Le nom du foyer et son fuseau horaire.</li>
            <li>Les membres et les personnes que tu y déclares (prénom, rôle adulte ou enfant).</li>
          </ul>

          <h3>Ce que tu confies à Colette</h3>
          <ul>
            <li>
              Les textes que tu écris, dictes ou transfères, tels que tu les as saisis, avec leur
              date.
            </li>
            <li>
              Ce que Colette en tire : événements, tâches, rappels, listes, et les liens entre eux.
            </li>
            <li>Un journal des actions sensibles du foyer, pour que rien ne se produise sans trace.</li>
          </ul>

          <h3>Ce que Colette ne conserve pas</h3>
          <ul>
            <li>
              <strong>Aucune donnée de localisation.</strong>
            </li>
            <li>
              <strong>Aucun accès à ta boîte mail</strong> : seuls les messages que tu transfères
              volontairement lui parviennent.
            </li>
            <li>
              <strong>Aucun traceur publicitaire</strong>, aucun outil de mesure d'audience, aucun
              cookie de suivi.
            </li>
          </ul>

          <h2>3. Pourquoi, et sur quel fondement</h2>
          <div className="v-defilant">
            <table className="v-tableau">
              <thead>
                <tr>
                  <th>Usage</th>
                  <th>Base légale</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Te donner accès à ton compte et à ton foyer</td>
                  <td>Exécution du service que tu as demandé</td>
                </tr>
                <tr>
                  <td>Comprendre tes messages et te proposer des éléments à valider</td>
                  <td>Exécution du service que tu as demandé</td>
                </tr>
                <tr>
                  <td>Sécurité, journal d'actions, sauvegardes</td>
                  <td>Intérêt légitime à protéger tes données</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            Colette ne fait <strong>aucun profilage publicitaire</strong> et ne prend aucune
            décision automatisée à ton sujet : elle propose, tu décides.
          </p>

          <h2>4. Qui d'autre voit ces données</h2>
          <p>
            Colette fait appel à deux prestataires, et à aucun autre. Aucune donnée n'est vendue,
            louée ou échangée.
          </p>
          <div className="v-defilant">
            <table className="v-tableau">
              <thead>
                <tr>
                  <th>Prestataire</th>
                  <th>Rôle</th>
                  <th>Lieu</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Hetzner Online GmbH</td>
                  <td>Hébergement du serveur et de la base de données</td>
                  <td>Nuremberg, Allemagne (Union européenne)</td>
                </tr>
                <tr>
                  <td>Anthropic (et, en secours, OpenAI)</td>
                  <td>Compréhension du texte que tu envoies à Colette</td>
                  <td>États-Unis</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>Ce qu'il faut savoir sur l'intelligence artificielle</h3>
          <p>
            Quand tu appuies sur « Organiser », <strong>le texte de ce message précis</strong> est
            transmis à Anthropic pour être analysé, puis la réponse revient à Colette. C'est le
            seul moment où un tiers voit ce que tu as écrit.
          </p>
          <ul>
            <li>
              Anthropic s'engage à <strong>ne pas utiliser</strong> les contenus transmis par son
              interface de programmation pour entraîner ses modèles.
            </li>
            <li>
              Colette conserve de son côté le <strong>coût et la durée</strong> de chaque appel
              (modèle utilisé, nombre de jetons), <strong>jamais le contenu</strong> du message.
            </li>
            <li>
              Ce transfert a lieu hors de l'Union européenne et repose sur les clauses
              contractuelles types de la Commission européenne.
            </li>
          </ul>
          <p>
            Rien d'autre ne part chez un tiers : ton planning, tes tâches et tes listes ne quittent
            jamais le serveur.
          </p>

          <h2>5. Combien de temps</h2>
          <ul>
            <li>
              Tes données sont conservées <strong>tant que ton compte existe</strong>.
            </li>
            <li>
              Une sauvegarde de la base est faite chaque nuit et conservée{' '}
              <strong>14 jours</strong>, puis détruite automatiquement. Après suppression de ton
              compte, tes données peuvent donc subsister au plus 14 jours dans ces sauvegardes.
            </li>
          </ul>

          <h2>6. Comment elles sont protégées</h2>
          <ul>
            <li>Tout passe par une liaison chiffrée (HTTPS).</li>
            <li>Les mots de passe sont hachés avec Argon2, algorithme conçu pour cet usage.</li>
            <li>
              La base de données n'est <strong>pas exposée à Internet</strong> : seul le serveur
              de Colette peut l'atteindre.
            </li>
            <li>
              Les données d'un foyer sont cloisonnées : un compte ne peut jamais lire celles d'un
              autre foyer. Ce cloisonnement est vérifié par des tests automatiques à chaque
              modification du code.
            </li>
          </ul>

          <h2>7. Tes droits</h2>
          <p>
            Trois de ces droits s'exercent <strong>directement dans l'application</strong>, sans
            rien demander à personne. Dans l'écran Colette, section « Mes données » :
          </p>
          <ul>
            <li>
              <strong>Emporter tes données</strong> — « Télécharger » écrit un fichier avec tout
              ce que Colette sait de toi : ta boîte, ton planning, tes listes, tes photos.
            </li>
            <li>
              <strong>Supprimer ton compte</strong> — ta boîte, tes photos et tout ce que toi seul
              pouvais voir disparaissent définitivement. Ce que tu as mis dans la vie du foyer y
              reste, sans ton nom. Si tu es la dernière personne du foyer, le foyer entier part
              avec toi.
            </li>
            <li>
              <strong>Corriger</strong> — les fiches du foyer, les propositions de Colette et tout
              ce qui est au planning se modifient là où ils s'affichent.
            </li>
          </ul>
          <p>
            Une limite honnête : les événements que Colette a écrits dans <em>ton</em> Google
            Agenda restent chez Google après la suppression de ton compte. Ils t'appartiennent, et
            c'est à toi de les y retirer.
          </p>
          <p>
            Pour tout le reste — t'opposer à un traitement, poser une question, obtenir de l'aide —
            écris à <a href={`mailto:${EMAIL_CONTACT}`}>{EMAIL_CONTACT}</a> : nous répondons sous
            un mois.
          </p>
          <p>
            Si la réponse ne te satisfait pas, tu peux saisir l'autorité de protection des données
            de ton pays — en France, la CNIL.
          </p>

          <h2>8. Cookies</h2>
          <p>
            Colette n'utilise <strong>aucun cookie</strong>. Ta session est conservée dans la
            mémoire locale de ton navigateur, uniquement pour t'éviter de te reconnecter à chaque
            ouverture. Elle disparaît quand tu te déconnectes.
          </p>

          <h2>9. Les enfants</h2>
          <p>
            Un compte enfant est créé par un adulte du foyer, qui reste responsable de son usage.
            Aucune donnée d'enfant n'est collectée en dehors de ce que l'adulte y inscrit.
          </p>

          <h2>10. Si ce texte change</h2>
          <p>
            Toute modification sera signalée dans l'application avant de prendre effet. La date en
            haut de cette page indique la dernière version.
          </p>

          <div style={{ marginTop: 'var(--e7)', display: 'flex', gap: 'var(--e3)', flexWrap: 'wrap' }}>
            <Lien vers="/" className="v-bouton v-bouton--principal">
              Retour à l'accueil
            </Lien>
            <Lien vers={CHEMIN_DEMO} className="v-bouton v-bouton--secondaire">
              Voir comment ça marche
            </Lien>
          </div>
        </div>
      </main>

      <Pied />
    </div>
  );
}
